import React, { Suspense } from 'react'

import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom'

import { Loading } from '@elgatoylacaja/components'
import { compose } from 'lodash/fp'
import { AppContext, AppContextData } from './contexts/AppContext'

const SobrePsicodelicos = React.lazy(() =>
  import(
    /* webpackChunkName: "SobrePsicodelicos" */ './views/SobrePsicodelicos'
  )
)
const MasSignificativo = React.lazy(() =>
  import(/* webpackChunkName: "MasSignificativo" */ './views/MasSignificativo')
)
const ConsumoPsicoactivos = React.lazy(() =>
  import(
    /* webpackChunkName: "ConsumoPsicoactivos" */ './views/ConsumoPsicoactivos'
  )
)
const UltimoConsumo = React.lazy(() =>
  import(/* webpackChunkName: "UltimoConsumo" */ './views/UltimoConsumo')
)
const MotivosConsumo = React.lazy(() =>
  import(/* webpackChunkName: "MotivosConsumo" */ './views/MotivosConsumo')
)
const CambiosConsumoPsicoactivos = React.lazy(() =>
  import(
    /* webpackChunkName: "CambiosConsumoPsicoactivos" */ './views/CambiosConsumoPsicoactivos'
  )
)
const SensacionActual = React.lazy(() =>
  import(/* webpackChunkName: "SensacionActual" */ './views/SensacionActual')
)
const SensacionGeneralmente = React.lazy(() =>
  import(
    /* webpackChunkName: "SensacionGeneralmente" */ './views/SensacionGeneralmente'
  )
)
const SensacionUltimaSemana = React.lazy(() =>
  import(
    /* webpackChunkName: "SensacionUltimaSemana" */ './views/SensacionUltimaSemana'
  )
)
const SensacionUltimoMes = React.lazy(() =>
  import(
    /* webpackChunkName: "SensacionUltimoMes" */ './views/SensacionUltimoMes'
  )
)
const RepresentacionFrases = React.lazy(() =>
  import(
    /* webpackChunkName: "RepresentacionFrases" */ './views/RepresentacionFrases'
  )
)
const LazyDemoRoutes = React.lazy(() =>
  import(/* webpackChunkName: "LazyDemoRoutes" */ './DemoRoutes')
)
const Thanks = React.lazy(() =>
  import(/* webpackChunkName: "Thanks" */ './views/Thanks')
)
const Terms = React.lazy(() =>
  import(/* webpackChunkName: "Terms" */ './views/Terms')
)
const Empezar = React.lazy(() =>
  import(/* webpackChunkName: "Empezar" */ './views/Empezar')
)
const ConsumoPsicodelicos = React.lazy(() =>
  import(
    /* webpackChunkName: "ConsumoPsicodelicos" */ './views/ConsumoPsicodelicos'
  )
)

type WrappedComponentType =
  | React.ComponentType<RouteComponentProps<any>>
  | React.ComponentType<any>

const condition = (appState: AppContextData) => (
  f: (x: AppContextData) => boolean
) => (r: string) => (WrappedComponent: WrappedComponentType) => (
  p: RouteComponentProps
) => {
  return p.match.isExact ? (
    f(appState) ? (
      <WrappedComponent {...p} />
    ) : (
      <Redirect to={r} {...p} />
    )
  ) : null
}

type LinkedRouteConfig = { prev?: string; route: string; next?: string }

class LinkedRoutes {
  paths: Array<[string, React.ComponentType<any>]>

  constructor(paths: Array<[string, React.ComponentType<any>]>) {
    this.paths = paths
  }

  config = () =>
    this.paths.map(([path, component], index, list): [
      LinkedRouteConfig,
      React.ComponentType
    ] => [
      {
        prev: index === 0 ? undefined : list[index - 1][0],
        route: path,
        next: index === list.length - 1 ? undefined : list[index + 1][0],
      },
      component,
    ])
}

const LazySwitchRoutes = () => {
  const { appState } = React.useContext(AppContext)

  const ExperimentRoutes = React.useMemo(() => {
    const {
      psicodelicos_consumidos,
      consumo_psicoactivos,
    } = appState.experiment
    const linkedRoutes = new LinkedRoutes([
      ['/terms', Terms],
      ['/empezar', Empezar],
      ['/consumo-psicodelicos', ConsumoPsicodelicos],
      ...(psicodelicos_consumidos !== undefined &&
      psicodelicos_consumidos.length !== 0
        ? [
            ['/sobre-psicodelicos', SobrePsicodelicos],
            ...(psicodelicos_consumidos.length === 1
              ? ([] as any)
              : [['/mas-significativo', MasSignificativo]]),
            ['/ultimo-consumo', UltimoConsumo],
            ['/motivos-consumo', MotivosConsumo],
          ]
        : []),
      ['/consumo-psicoactivos', ConsumoPsicoactivos],
      ...(consumo_psicoactivos !== undefined &&
      consumo_psicoactivos.psicoactivos_cosumidos.length !== 0
        ? [['/cambios-psicoactivos', CambiosConsumoPsicoactivos]]
        : ([] as any)),
      ['/sensacion-actual', SensacionActual],
      ['/sensacion-generalmente', SensacionGeneralmente],
      ['/sensacion-ultima-semana', SensacionUltimaSemana],
      ['/sensacion-ultimo-mes', SensacionUltimoMes],
      ['/representacion-frases', RepresentacionFrases],
      ['/thanks', Thanks],
    ])
    return linkedRoutes.config()
  }, [appState.experiment])

  const stateCondition = condition(appState)

  const alwaysTrue = stateCondition(_ => true)('')

  const visited = (r: string) =>
    stateCondition(state => state.visited.includes(r))(r)

  const notVisited = (r: string) =>
    stateCondition(state => !state.visited.includes(r))

  const requirePrevious = ({ prev, route, next }: LinkedRouteConfig) =>
    compose(
      prev !== undefined ? visited(prev) : alwaysTrue,
      next !== undefined ? notVisited(route)(next) : alwaysTrue
    )

  return (
    <Switch>
      <Redirect from="/" exact to="/terms" />
      <Suspense fallback={<Loading loading fixed />}>
        {ExperimentRoutes.map(([config, component]) => (
          <Route
            key={config.route}
            path={config.route}
            exact
            render={requirePrevious(config)(component)}
          />
        ))}

        {process.env.REACT_APP_ENVIRONMENT !== 'production' ? (
          <LazyDemoRoutes appState={appState} />
        ) : null}
      </Suspense>
    </Switch>
  )
}

export default LazySwitchRoutes
